import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCameraState } from 'store/camera/camera.selectors';
import { selectPreloaderVideos } from 'store/preloader/preloader.selectors';
import { cameraActions } from 'store/camera/camera.actions';
import { useTranslation } from 'react-i18next';
import {
    Question,
    RecorderDescription,
    WatchVideoAgainContainer,
    WatchVideoAgainMessage,
    RecorderContainer,
    RecorderFooter,
    ButtonContainer,
    VideoLoadingPlaceholder,
} from './InterviewController.styled';
import { AppLayout } from 'styles/shared.style';

import InterviewIntroduction from '../InterviewIntroduction/InterviewIntroduction';
import VideoComponent from 'components/shared/VideoComponent/VideoComponent';
import ShadowButton from 'components/shared/ShadowButton/ShadowButton';
import InterviewRecorder from '../InterviewRecorder/InterviewRecorder';
import ModuleIntroductionView from 'components/shared/ModuleIntroductionView/ModuleIntroductionView';

import { ReactComponent as BackButton } from 'assets/icons/video-controls/back-button.svg';
import { getInterviewVideoId } from 'utils/module-identificators';
import { RotateSpinLoader } from 'react-css-loaders';
import CameraService from '../../../services/CameraService';
import {
    preloaderActions,
    preloaderActionTypes,
} from 'store/preloader/preloader.actions';

const videoModuleId = (currentQuestion) =>
    getInterviewVideoId(currentQuestion.questionId);
const buildQuestion = (question, questionIndex) => ({
    ...question,
    submitted: false,
    playingVideo: !!question.videos,
    shouldDisplayQuestionSubmitted: questionIndex !== 0,
});

const InterviewController = (props) => {
    const { invitationId, module, onComplete } = props;

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [introductionAccepted, setIntroductionAccepted] = useState(false);
    const lastQuestion = module.questions[module.questions.length - 1];
    const [currentQuestion, setCurrentQuestion] = useState(
        buildQuestion(module.questions[0], 0),
    );

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const cameraState = useSelector(selectCameraState);
    const preloader = useSelector(selectPreloaderVideos);
    const blob = CameraService.getBlob();

    // Start capturing user media for written questions
    useEffect(() => {
        if (
            introductionAccepted &&
            !currentQuestion.shouldDisplayQuestionSubmitted &&
            !currentQuestion.videos &&
            !blob
        ) {
            dispatch(cameraActions.captureUserMedia());
        }
    }, [currentQuestion, introductionAccepted]); // eslint-disable-line react-hooks/exhaustive-deps -- fallow only the changes of the current question to start capturing

    // Re-dispatch preload for any interview question whose preload entry is in
    // a terminal-failed or never-started state. The saga's dedupe logic skips
    // entries that are already loading or already ready, so this is a no-op
    // for the success path and only retries the videos that actually need it.
    // Without this, a transient preload failure (network blip, 120s timeout)
    // leaves the candidate on an indefinite spinner when they reach the
    // question — there's no other recovery path that keeps to the
    // "fully-buffered before playback" requirement.
    useEffect(() => {
        module.questions.forEach((q) => {
            if (!q.videos) return;
            const videoId = getInterviewVideoId(q.questionId);
            const state = preloader[videoId];
            const needsRetry = !state || (!state.loading && !state.ready);
            if (needsRetry) {
                const url = q.videos.low_res || q.videos.high_res;
                if (url) {
                    dispatch(preloaderActions.preloadVideo(videoId, url));
                }
            }
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

    // Watchdog for the *current* question's video: if it has been stuck in
    // `loading: true` for too long (the original preload XHR is hung or just
    // hasn't completed yet), force a fresh fetch by dispatching
    // PRELOAD_VIDEO_FETCHING directly. That bypasses the dedupe in the
    // preloadVideo saga (which would otherwise skip retries while loading is
    // true). The forceRetryAttemptedRef ensures we only force-retry once per
    // video per session so we don't pile concurrent XHRs.
    //
    // We track the latest preloader state via a ref instead of an effect
    // dependency: depending on `preloader` directly would re-run this effect
    // every time *any* video's state changes (other questions loading,
    // culture-fit finishing, etc.), constantly clearing and restarting the
    // 15s timer so it would never fire.
    const forceRetryAttemptedRef = useRef(new Set());
    const preloaderRef = useRef(preloader);
    useEffect(() => {
        preloaderRef.current = preloader;
    }, [preloader]);
    useEffect(() => {
        if (!currentQuestion.videos) return undefined;
        const videoId = getInterviewVideoId(currentQuestion.questionId);
        const state = preloaderRef.current[videoId];
        if (!state || state.ready || !state.loading) return undefined;
        if (forceRetryAttemptedRef.current.has(videoId)) return undefined;

        const url =
            currentQuestion.videos.low_res || currentQuestion.videos.high_res;
        if (!url) return undefined;

        const watchdog = setTimeout(() => {
            // Re-check latest state before forcing — the original preload may
            // have completed in the last 15s, in which case we leave it alone.
            const latest = preloaderRef.current[videoId];
            if (latest && latest.ready) return;
            forceRetryAttemptedRef.current.add(videoId);
            dispatch({
                type: preloaderActionTypes.PRELOAD_VIDEO_FETCHING,
                payload: { videoId, videoUrl: url },
            });
        }, 8000);

        return () => clearTimeout(watchdog);
    }, [currentQuestion, dispatch]);

    const onRecordingAnswer = () => {
        dispatch(
            cameraActions.recordUserMedia(
                invitationId,
                `interview-${currentQuestionIndex + 1}`,
                false,
            ),
        );
    };

    const onStopRecordingAnswer = () => {
        dispatch(cameraActions.stopRecordingUserMedia(invitationId));
    };

    const onQuestionVideoComplete = () => {
        dispatch(cameraActions.captureUserMedia());
        setCurrentQuestion({
            ...currentQuestion,
            playingVideo: false,
        });
    };

    const watchVideoAgain = () => {
        setCurrentQuestion({
            ...currentQuestion,
            playingVideo: true,
        });
    };

    const onSubmitQuestion = () => {
        dispatch(cameraActions.dispatchBlobUpload(invitationId));
        updateQuestion();
    };

    const updateQuestion = () => {
        if (currentQuestionIndex + 1 === module.questions.length) {
            setCurrentQuestion(null);
            setCurrentQuestionIndex(currentQuestionIndex + 1);

            onComplete();
        } else {
            const nextQuestionIndex = currentQuestionIndex + 1;
            const nextQuestion = buildQuestion(
                module.questions[nextQuestionIndex],
                nextQuestionIndex,
            );

            setCurrentQuestion(nextQuestion);
            setCurrentQuestionIndex(nextQuestionIndex);
        }
    };

    const renderInterviewRecorder = () => {
        return (
            <AppLayout>
                {/* Button: Watch again */}
                {!!blob &&
                    !cameraState.isCameraRecording &&
                    !!currentQuestion.videos && (
                        <WatchVideoAgainContainer>
                            <BackButton
                                id="back-button"
                                className="InterviewController__BackButton"
                                onClick={() => watchVideoAgain()}
                            />
                            <WatchVideoAgainMessage
                                onClick={() => watchVideoAgain()}>
                                {t(
                                    'SPEAKING_MODULE.INTERVIEW_RECORDER.WATCH_QUESTION_AGAIN_MESSAGE',
                                )}
                            </WatchVideoAgainMessage>
                        </WatchVideoAgainContainer>
                    )}

                {/* Question text */}
                <Question>{currentQuestion.question}</Question>
                <RecorderDescription>
                    {t('SPEAKING_MODULE.INTERVIEW_RECORDER.MESSAGE')}
                </RecorderDescription>

                <React.Fragment>
                    {/* Recorder */}
                    <RecorderContainer>
                        <InterviewRecorder
                            videoStream={CameraService.videoStream}
                            blob={CameraService.getBlob()}
                            cameraState={cameraState}
                            onRecord={() => onRecordingAnswer()}
                            onStopRecording={() => onStopRecordingAnswer()}
                        />
                    </RecorderContainer>

                    {/* Button: Submit answer */}
                    <RecorderFooter>
                        {!!blob && (
                            <ButtonContainer>
                                <ShadowButton
                                    onClick={() => {
                                        onSubmitQuestion();
                                    }}>
                                    {t(
                                        'SPEAKING_MODULE.INTERVIEW_RECORDER.SUBMIT_BUTTON',
                                    )}
                                </ShadowButton>
                            </ButtonContainer>
                        )}
                    </RecorderFooter>
                </React.Fragment>
            </AppLayout>
        );
    };

    const renderVideoQuestion = () => {
        const videoId = videoModuleId(currentQuestion);
        const videoPreloaded = preloader[videoId];
        if (videoPreloaded && videoPreloaded.ready) {
            return (
                <VideoComponent
                    videoId={videoId}
                    videoUrl={videoPreloaded.url}
                    fallbackVideoUrl={videoPreloaded.fallbackUrl}
                    playAutomatically={true}
                    onComplete={() => onQuestionVideoComplete()}
                    isVideoFullscreen={true}
                />
            );
        }
        // Preload not ready yet. Distinguish "still loading" from
        // "failed once, retry pending" so the candidate gets a meaningful
        // message instead of staring at an unexplained spinner.
        const hasFailed =
            videoPreloaded && !videoPreloaded.loading && !videoPreloaded.ready;
        return (
            <VideoLoadingPlaceholder>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <RotateSpinLoader color="#fff" size="2" />
                    <p
                        style={{
                            color: '#fff',
                            marginTop: 16,
                            textAlign: 'center',
                        }}>
                        {hasFailed
                            ? t(
                                  'SPEAKING_MODULE.INTERVIEW_RECORDER.VIDEO_RETRY',
                                  {
                                      defaultValue:
                                          'Loading the video took longer than expected. Retrying…',
                                  },
                              )
                            : t(
                                  'SPEAKING_MODULE.INTERVIEW_RECORDER.VIDEO_LOADING',
                                  {
                                      defaultValue:
                                          'Loading the video, please wait…',
                                  },
                              )}
                    </p>
                </div>
            </VideoLoadingPlaceholder>
        );
    };

    const onAcceptQuestionIntroduction = () => {
        setCurrentQuestion({
            ...currentQuestion,
            shouldDisplayQuestionSubmitted: false,
        });
    };

    return (
        <React.Fragment>
            {!introductionAccepted && (
                <InterviewIntroduction
                    numberOfQuestions={module.questions.length}
                    onNext={() => setIntroductionAccepted(true)}
                />
            )}
            {introductionAccepted && currentQuestion && (
                <React.Fragment>
                    {currentQuestion.shouldDisplayQuestionSubmitted &&
                        currentQuestion.questionId !==
                            lastQuestion.questionId && (
                            <ModuleIntroductionView
                                buttonPlaceholder={t(
                                    'SPEAKING_MODULE.NEXT_QUESTION_INTRODUCTION.NEXT_BUTTON',
                                )}
                                onNext={() => onAcceptQuestionIntroduction()}>
                                <h2>
                                    {t(
                                        'SPEAKING_MODULE.NEXT_QUESTION_INTRODUCTION.TITLE',
                                    )}
                                </h2>
                                <p>
                                    {t(
                                        'SPEAKING_MODULE.NEXT_QUESTION_INTRODUCTION.MESSAGE',
                                    )}
                                </p>
                            </ModuleIntroductionView>
                        )}
                    {currentQuestion.shouldDisplayQuestionSubmitted &&
                        currentQuestion.questionId ===
                            lastQuestion.questionId && (
                            <ModuleIntroductionView
                                buttonPlaceholder={t(
                                    'SPEAKING_MODULE.LAST_QUESTION_INTRODUCTION.NEXT_BUTTON',
                                )}
                                onNext={() => onAcceptQuestionIntroduction()}>
                                <h2>
                                    {t(
                                        'SPEAKING_MODULE.LAST_QUESTION_INTRODUCTION.TITLE',
                                    )}
                                </h2>

                                <p>
                                    {t(
                                        'SPEAKING_MODULE.LAST_QUESTION_INTRODUCTION.MESSAGE',
                                    )}
                                </p>
                            </ModuleIntroductionView>
                        )}
                    {!currentQuestion.shouldDisplayQuestionSubmitted &&
                        currentQuestion.videos &&
                        currentQuestion.playingVideo &&
                        renderVideoQuestion()}
                    {!currentQuestion.shouldDisplayQuestionSubmitted &&
                        (!currentQuestion.videos ||
                            !currentQuestion.playingVideo) &&
                        renderInterviewRecorder()}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default InterviewController;
